import { PropsWithChildren } from 'preact/compat';
import { useCallback, useRef, useState } from 'preact/hooks';
import './Chat.less';
import ChatInput from './ChatInput';
import { LoadingStatus } from '../../models/LoadingStatus';
import type { ChatMessage as ChatMessageModel } from '../../models/ChatMessage';
import ChatMessage from './ChatMessage';
import LoadingChatMessage from './LoadingChatMessage';
import { useTranslation } from '../../hooks/useTranslation';
import ChatError from './ChatError';

export type ChatProps = PropsWithChildren<{
    disabled?: boolean;
    onSubmit: (sentence: string) => void;
    inputPlaceholder?: string;
    header?: JSX.Element;
    stickyHeader?: JSX.Element;
    chatMessages?: ChatMessageModel[];
    status: LoadingStatus;
}>;

const Chat = ({
    disabled,
    onSubmit,
    inputPlaceholder,
    children,
    header,
    stickyHeader,
    chatMessages,
    status
}: ChatProps) => {
    const { t } = useTranslation();
    const [isStickyHeaderVisible, setStickyHeaderVisible] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);
    const scrollableRef = useRef<HTMLDivElement>(null);
    const onScroll = useCallback(() => {
        if (!scrollableRef.current || !headerRef.current) {
            return;
        }
        const isInView = checkInView(scrollableRef.current, headerRef.current);
        if (!isInView && !isStickyHeaderVisible) {
            setStickyHeaderVisible(true);
        } else if (isInView && isStickyHeaderVisible) {
            setStickyHeaderVisible(false);
        }
    }, [scrollableRef.current, headerRef.current, isStickyHeaderVisible]);
    return (
        <div className={`bri-Chat`}>
            <div
                ref={scrollableRef}
                className="bri-Chat-messages bri-SlimScroll"
                onScroll={onScroll}
            >
                {stickyHeader && (
                    <div className={`bri-Chat-sticky-header-wrapper`}>
                        <div
                            className={`bri-Chat-sticky-header ${
                                isStickyHeaderVisible ? 'bri-visible' : ''
                            }`}
                        >
                            {stickyHeader}
                        </div>
                    </div>
                )}
                {header && (
                    <div className="bri-Chat-header" ref={headerRef}>
                        {header}
                    </div>
                )}
                {children}
                {chatMessages &&
                    chatMessages.map((chatMessage, index) => (
                        <ChatMessage
                            chatMessage={chatMessage}
                            key={chatMessage.uuid}
                        />
                    ))}
                {status == 'loading' && <LoadingChatMessage />}
            </div>
            {status === 'error' && (
                <ChatError message={t('PRODUCT_DETAIL_CHAT_ERROR')} />
            )}
            <ChatInput
                onSubmit={onSubmit}
                placeholder={inputPlaceholder}
                disabled={disabled}
                disclaimer={t('CHAT_DISCLAIMER')}
            />
        </div>
    );
};
export default Chat;

function checkInView(container: HTMLDivElement, element: HTMLDivElement) {
    //Get container properties
    const cTop = container.scrollTop;
    // const cBottom = cTop + container.clientHeight;

    //Get element properties
    const eTop = element.offsetTop;
    // const eBottom = eTop + element.clientHeight;

    //Check if in view
    // const isTotal = eTop >= cTop && eBottom <= cBottom;
    const isTotal = eTop >= cTop;

    //Return outcome
    return isTotal;
}
